import { useId, useState } from 'react';
import { useGiveSecret } from '../hooks/use-session-actions.js';

// « Give a secret » (ADR-043 section 2): a credential for this session's task, written to its own file by the agent,
// never in the conversation. The value is cleared as soon as it is sent; only its name is shown after.

const NAME = /^[A-Z][A-Z0-9_]{1,63}$/;

const input = 'rounded-md border px-2 py-1.5 text-xs focus-visible:outline focus-visible:outline-2';
const inputStyle = { backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border-2)', color: 'var(--color-text)' };

export function GiveSecret({ serverId, sessionId }: { serverId: string; sessionId: string }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [given, setGiven] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const give = useGiveSecret(serverId, sessionId);
  const id = useId();
  const nameOk = NAME.test(name);

  const close = () => { setOpen(false); setName(''); setValue(''); setFailure(null); give.reset(); };
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nameOk || !value) return;
    setFailure(null);
    give.mutate({ name, value }, {
      // The mutation keeps what it sent: reset, so the value is held nowhere once the answer came
      onSuccess: (r) => { setGiven(r.name); setValue(''); setName(''); setOpen(false); give.reset(); },
      // The value goes on failure too: typed again rather than kept in the page; the message stays
      onError: (error) => { setValue(''); setFailure(error instanceof Error ? error.message : String(error)); give.reset(); },
    });
  };

  if (!open) {
    return (
      <span className="flex items-center gap-2">
        <button type="button" onClick={() => { setOpen(true); setGiven(null); }} aria-expanded={false} aria-controls={id}
          className="text-xs hover:underline focus-visible:outline focus-visible:outline-2" style={{ color: 'var(--color-brand-light)' }}
          title="A credential for this session's task, kept out of the conversation">
          Give a secret
        </button>
        {given && (
          <span role="status" className="text-xs" style={{ color: 'var(--color-text-2)' }}>
            {`${given} given: the session uses it from its file; it goes when the session stops.`}
          </span>
        )}
      </span>
    );
  }
  return (
    <form id={id} onSubmit={submit} aria-label="Give the session a secret"
      className="flex w-full flex-wrap items-center gap-2 rounded-md border p-2" style={{ borderColor: 'var(--color-border-2)', backgroundColor: 'var(--color-surface-2)' }}>
      <input type="text" value={name} onChange={(e) => setName(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
        placeholder="GH_ADMIN_TOKEN" aria-label="Name of the secret" autoComplete="off" spellCheck={false} maxLength={64}
        className={`${input} font-mono`} style={{ ...inputStyle, width: '12rem' }} />
      <input type="password" value={value} onChange={(e) => setValue(e.target.value)}
        placeholder="Value" aria-label="Value of the secret" autoComplete="new-password" spellCheck={false}
        className={`${input} min-w-0 flex-1`} style={{ ...inputStyle, minWidth: '10rem' }} />
      <button type="submit" disabled={!nameOk || !value || give.isPending}
        className="rounded-md px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50 max-sm:min-h-10" style={{ backgroundColor: 'var(--color-brand)' }}>
        {give.isPending ? 'Giving…' : 'Give'}
      </button>
      <button type="button" onClick={close} className="px-2 py-1.5 text-xs hover:underline max-sm:min-h-10" style={{ color: 'var(--color-text-2)' }}>Cancel</button>
      <p className="w-full text-xs" style={{ color: failure ? 'var(--color-error)' : 'var(--color-text-3)' }} role={failure ? 'alert' : undefined}>
        {failure
          ? `Not given: ${failure}`
          : 'Written to a file of this session only, masked in what its commands print, removed when it stops. Revoke it at its source when the task is done.'}
      </p>
    </form>
  );
}
